import { useCallback, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'

import { api } from '@/services/api.service'
import ROUTES from '@/ROUTES'
import AdminPageHeader from '@/components/AdminPageHeader'

// ---------- Types ----------

interface SystemStatusData {
    api: { status: 'ok' | 'error' }
    database: { status: 'ok' | 'error'; error?: string }
    openai: {
        status: 'ok' | 'error'
        error?: string
        code?: string
        keyConfigured: boolean
    }
    stripe: {
        mode: 'test' | 'live'
        status: 'ok' | 'error'
        error?: string
        keyConfigured: boolean
        keyRedacted?: string
        webhookConfigured: boolean
    }
    keys: {
        openai: string | null
        stripe: string | null
        database: string | null
    }
    alerting: { snsConfigured: boolean }
    errors: {
        last24h: number
        byService: { _id: string; count: number; lastError: string; lastOccurred: string }[]
    }
}

interface LogEntry {
    _id: string
    timestamp: string
    level: string
    service?: string
    message: string
    error?: any
    data?: any
}

interface LogsResponse {
    logs: LogEntry[]
    total: number
}

// ---------- Status Dot ----------

function StatusDot({ ok, pulse }: { ok: boolean; pulse?: boolean }) {
    return (
        <div
            className={`h-2.5 w-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'} ${!ok && pulse ? 'animate-pulse' : ''}`}
        />
    )
}

// ---------- Health Cards ----------

function ServiceCard({
    title,
    ok,
    children,
}: {
    title: string
    ok: boolean
    children?: React.ReactNode
}) {
    const bg = ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
    return (
        <div className={`rounded-xl border p-5 shadow-sm ${bg}`}>
            <div className='flex items-center justify-between mb-3'>
                <h3 className='text-sm font-semibold text-gray-700'>{title}</h3>
                <div className='flex items-center gap-2'>
                    <StatusDot ok={ok} pulse />
                    <span className={`text-sm font-medium ${ok ? 'text-green-600' : 'text-red-600'}`}>
                        {ok ? 'Operational' : 'Down'}
                    </span>
                </div>
            </div>
            {children}
        </div>
    )
}

function ApiCard({ status }: { status: SystemStatusData | null }) {
    if (!status) return <CardSkeleton title='API' />
    return <ServiceCard title='API' ok={status.api.status === 'ok'} />
}

function DatabaseCard({ status }: { status: SystemStatusData | null }) {
    if (!status) return <CardSkeleton title='Database' />
    const ok = status.database.status === 'ok'
    return (
        <ServiceCard title='Database' ok={ok}>
            <div className='text-xs text-gray-500'>
                {status.keys.database && (
                    <span className='font-mono'>{status.keys.database}</span>
                )}
            </div>
            {!ok && status.database.error && (
                <p className='mt-2 text-sm text-red-700'>{status.database.error}</p>
            )}
        </ServiceCard>
    )
}

function OpenAiCard({ status }: { status: SystemStatusData | null }) {
    if (!status) return <CardSkeleton title='OpenAI' />
    const isOk = status.openai.status === 'ok'

    return (
        <ServiceCard title='OpenAI' ok={isOk}>
            {!isOk && (
                <div className='mb-3 p-3 bg-white/80 rounded-lg'>
                    <p className='text-sm font-medium text-red-700'>{status.openai.error}</p>
                    {status.openai.code && (
                        <p className='text-xs text-red-500 mt-1'>Code: {status.openai.code}</p>
                    )}
                </div>
            )}

            <div className='grid grid-cols-3 gap-3 text-center'>
                <div>
                    <p className='text-xs text-gray-500'>API Key</p>
                    <p className={`text-sm font-medium ${status.openai.keyConfigured ? 'text-green-600' : 'text-red-600'}`}>
                        {status.openai.keyConfigured ? 'Configured' : 'Missing'}
                    </p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Errors (24h)</p>
                    <p className={`text-sm font-medium ${status.errors.last24h > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {status.errors.last24h}
                    </p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Key</p>
                    <p className='text-xs font-mono text-gray-600 truncate'>
                        {status.keys.openai || '—'}
                    </p>
                </div>
            </div>

            {status.errors.byService.length > 0 && (
                <div className='mt-3 pt-3 border-t border-gray-200/50'>
                    <p className='text-xs font-medium text-gray-500 mb-2'>Errors by Service (24h)</p>
                    <div className='space-y-1'>
                        {status.errors.byService.map(s => (
                            <div key={s._id} className='flex justify-between text-xs'>
                                <span className='text-gray-600'>{s._id || 'Unknown'}</span>
                                <span className='font-medium text-red-600'>{s.count} errors</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </ServiceCard>
    )
}

function StripeCard({ status }: { status: SystemStatusData | null }) {
    if (!status) return <CardSkeleton title='Stripe' />
    const ok = status.stripe.status === 'ok'
    const modeBadge = status.stripe.mode === 'live'
        ? 'bg-green-100 text-green-800'
        : 'bg-yellow-100 text-yellow-800'

    return (
        <ServiceCard title='Stripe' ok={ok}>
            <div className='flex items-center gap-2 mb-3'>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${modeBadge}`}>
                    {status.stripe.mode}
                </span>
            </div>

            {!ok && status.stripe.error && (
                <div className='mb-3 p-3 bg-white/80 rounded-lg'>
                    <p className='text-sm font-medium text-red-700'>{status.stripe.error}</p>
                </div>
            )}

            <div className='grid grid-cols-3 gap-3 text-center'>
                <div>
                    <p className='text-xs text-gray-500'>API Key</p>
                    <p className={`text-sm font-medium ${status.stripe.keyConfigured ? 'text-green-600' : 'text-red-600'}`}>
                        {status.stripe.keyConfigured ? 'Configured' : 'Missing'}
                    </p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Webhook</p>
                    <p className={`text-sm font-medium ${status.stripe.webhookConfigured ? 'text-green-600' : 'text-red-600'}`}>
                        {status.stripe.webhookConfigured ? 'Configured' : 'Missing'}
                    </p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Key</p>
                    <p className='text-xs font-mono text-gray-600 truncate'>
                        {status.keys.stripe || '—'}
                    </p>
                </div>
            </div>
        </ServiceCard>
    )
}

function AlertingCard({ status }: { status: SystemStatusData | null }) {
    if (!status) return <CardSkeleton title='Alerting' />
    const configured = status.alerting.snsConfigured

    return (
        <div className={`rounded-xl border p-5 shadow-sm ${configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className='flex items-center justify-between'>
                <h3 className='text-sm font-semibold text-gray-700'>SNS Alerting</h3>
                <span className={`text-sm font-medium ${configured ? 'text-green-600' : 'text-yellow-600'}`}>
                    {configured ? 'Active' : 'Not Configured'}
                </span>
            </div>
        </div>
    )
}

function CardSkeleton({ title }: { title: string }) {
    return (
        <div className='bg-white rounded-xl border border-gray-200 p-5 shadow-sm'>
            <h3 className='text-sm font-semibold text-gray-700 mb-3'>{title}</h3>
            <div className='flex items-center gap-2 text-sm text-gray-400'>
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-500' />
                Checking...
            </div>
        </div>
    )
}

// ---------- Log Level Badge ----------

function LevelBadge({ level }: { level: string }) {
    const colors: Record<string, string> = {
        error: 'bg-red-100 text-red-800',
        warn: 'bg-yellow-100 text-yellow-800',
        log: 'bg-gray-100 text-gray-800',
        debug: 'bg-blue-100 text-blue-800',
    }
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[level] || colors.log}`}>
            {level}
        </span>
    )
}

// ---------- Log Table ----------

function LogTable({
    logs,
    total,
    loading,
    level,
    service,
    onLevelChange,
    onServiceChange,
    onLoadMore,
}: {
    logs: LogEntry[]
    total: number
    loading: boolean
    level: string
    service: string
    onLevelChange: (v: string) => void
    onServiceChange: (v: string) => void
    onLoadMore: () => void
}) {
    const [expandedId, setExpandedId] = useState<string | null>(null)

    return (
        <div className='bg-white rounded-xl border border-gray-200 shadow-sm'>
            <div className='p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3'>
                <h3 className='text-sm font-semibold text-gray-700'>Recent Logs</h3>
                <div className='flex items-center gap-3'>
                    <select
                        value={level}
                        onChange={e => onLevelChange(e.target.value)}
                        className='rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500'
                    >
                        <option value='error'>Errors</option>
                        <option value='warn'>Warnings</option>
                        <option value='all'>All Levels</option>
                    </select>
                    <input
                        type='text'
                        value={service}
                        onChange={e => onServiceChange(e.target.value)}
                        placeholder='Filter by service...'
                        className='rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-44'
                    />
                    <span className='text-xs text-gray-400'>{total} total</span>
                </div>
            </div>

            <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                        <tr>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Time</th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Level</th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Service</th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Message</th>
                        </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                        {logs.map(log => (
                            <LogRow
                                key={log._id}
                                log={log}
                                expanded={expandedId === log._id}
                                onToggle={() => setExpandedId(expandedId === log._id ? null : log._id)}
                            />
                        ))}
                        {logs.length === 0 && !loading && (
                            <tr>
                                <td colSpan={4} className='px-4 py-8 text-center text-sm text-gray-400'>
                                    No log entries found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {loading && (
                <div className='flex items-center justify-center py-4'>
                    <div className='h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-brand-500' />
                </div>
            )}

            {logs.length < total && !loading && (
                <div className='p-4 border-t border-gray-200 text-center'>
                    <button
                        onClick={onLoadMore}
                        className='text-sm font-medium text-brand-600 hover:text-brand-700'
                    >
                        Load more ({total - logs.length} remaining)
                    </button>
                </div>
            )}
        </div>
    )
}

function LogRow({ log, expanded, onToggle }: { log: LogEntry; expanded: boolean; onToggle: () => void }) {
    const hasDetails = log.error || log.data

    return (
        <>
            <tr
                className={`${hasDetails ? 'cursor-pointer hover:bg-gray-50' : ''} ${expanded ? 'bg-gray-50' : ''}`}
                onClick={hasDetails ? onToggle : undefined}
            >
                <td className='px-4 py-3 text-xs text-gray-500 whitespace-nowrap'>
                    {format(new Date(log.timestamp), 'MMM d, h:mm:ss a')}
                </td>
                <td className='px-4 py-3 whitespace-nowrap'>
                    <LevelBadge level={log.level} />
                </td>
                <td className='px-4 py-3 text-xs text-gray-600 whitespace-nowrap font-mono'>
                    {log.service || '—'}
                </td>
                <td className='px-4 py-3 text-sm text-gray-700 max-w-md truncate'>
                    {log.message}
                    {hasDetails && (
                        <span className='ml-2 text-xs text-gray-400'>{expanded ? '▾' : '▸'}</span>
                    )}
                </td>
            </tr>
            {expanded && hasDetails && (
                <tr>
                    <td colSpan={4} className='px-4 py-3 bg-gray-50'>
                        <pre className='text-xs text-gray-600 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto'>
                            {JSON.stringify({ error: log.error, data: log.data }, null, 2)}
                        </pre>
                    </td>
                </tr>
            )}
        </>
    )
}

// ---------- Main Page ----------

export default function AdminSystem() {
    const [status, setStatus] = useState<SystemStatusData | null>(null)
    const [statusLoading, setStatusLoading] = useState(true)

    const [logs, setLogs] = useState<LogEntry[]>([])
    const [logTotal, setLogTotal] = useState(0)
    const [logLoading, setLogLoading] = useState(true)
    const [logLevel, setLogLevel] = useState('error')
    const [logService, setLogService] = useState('')

    const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null)

    const fetchStatus = useCallback(() => {
        api<SystemStatusData>(ROUTES.admin.systemStatus)
            .then(res => { if (res.data) setStatus(res.data) })
            .finally(() => setStatusLoading(false))
    }, [])

    const fetchLogs = useCallback((append = false) => {
        setLogLoading(true)
        const offset = append ? logs.length : 0
        const params = new URLSearchParams({ limit: '50', offset: String(offset) })
        if (logLevel !== 'all') params.set('level', logLevel)
        if (logService) params.set('service', logService)

        api<LogsResponse>(`${ROUTES.admin.systemLogs}?${params}`)
            .then(res => {
                if (res.data) {
                    setLogs(prev => append ? [...prev, ...res.data!.logs] : res.data!.logs)
                    setLogTotal(res.data.total)
                }
            })
            .finally(() => setLogLoading(false))
    }, [logLevel, logService, logs.length])

    // Fetch status on mount + auto-refresh every 30s
    useEffect(() => {
        fetchStatus()
        refreshTimer.current = setInterval(fetchStatus, 30000)
        return () => { if (refreshTimer.current) clearInterval(refreshTimer.current) }
    }, [fetchStatus])

    // Fetch logs when filters change
    useEffect(() => {
        fetchLogs(false)
    }, [logLevel, logService]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className='flex flex-col w-full max-w-7xl gap-5 py-10 mx-auto'>
            <AdminPageHeader
                heading='System Status'
                subheading='Service health, configuration, and error logs'
                action={
                    <button
                        onClick={() => { setStatusLoading(true); fetchStatus(); fetchLogs(false) }}
                        disabled={statusLoading}
                        className='rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50'
                    >
                        {statusLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                }
            />

            {/* Service Health Cards */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 px-4 sm:px-6 lg:px-8'>
                <ApiCard status={status} />
                <DatabaseCard status={status} />
                <OpenAiCard status={status} />
                <StripeCard status={status} />
            </div>

            {/* Alerting */}
            <div className='px-4 sm:px-6 lg:px-8'>
                <AlertingCard status={status} />
            </div>

            {/* Redacted Keys */}
            {status && (
                <div className='px-4 sm:px-6 lg:px-8'>
                    <div className='bg-white rounded-xl border border-gray-200 p-5 shadow-sm'>
                        <h3 className='text-sm font-semibold text-gray-700 mb-3'>Configured Keys</h3>
                        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                            {Object.entries(status.keys).map(([name, value]) => (
                                <div key={name}>
                                    <p className='text-xs text-gray-500 uppercase'>{name}</p>
                                    <p className='text-sm font-mono text-gray-700 truncate'>{value || '—'}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Error Log */}
            <div className='px-4 sm:px-6 lg:px-8'>
                <LogTable
                    logs={logs}
                    total={logTotal}
                    loading={logLoading}
                    level={logLevel}
                    service={logService}
                    onLevelChange={v => { setLogLevel(v); setLogs([]) }}
                    onServiceChange={v => { setLogService(v); setLogs([]) }}
                    onLoadMore={() => fetchLogs(true)}
                />
            </div>
        </div>
    )
}
